<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <!-- Contenedor Principal (Con efecto borroso si se abre cualquier modal) -->
    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showCreateModal || showAssignModal }"
    >
      <!-- Encabezado Estándar -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <Package class="w-7 h-7 text-[#F36421]" />
            Central de Pedidos & Despacho LLEVO
          </h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">
            Monitorea solicitudes, asigna motorizados de tu flota y audita fletes contra entrega (COD).
          </p>
        </div>
        <button
          @click="openCreateModal"
          class="flex items-center gap-2 px-5 py-2.5 bg-[#F36421] hover:bg-[#d95214] text-white font-bold rounded-xl shadow-lg shadow-[#F36421]/30 transition-all active:scale-95 cursor-pointer"
        >
          <Plus class="w-5 h-5" /> Nueva Solicitud / Cotizar
        </button>
      </div>

      <!-- Barra de Filtros Operativos -->
      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <!-- Búsqueda rápida -->
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por código de guía, cliente, teléfono o dirección..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all text-slate-800 dark:text-white"
          >
        </div>

        <!-- Filtro por Estado -->
        <select
          v-model="statusFilter"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-semibold focus:outline-none text-slate-700 dark:text-slate-200"
        >
          <option value="">Todos los Estados</option>
          <option value="PENDING">🟡 PENDIENTES (Por Asignar Chofer)</option>
          <option value="ASSIGNED">🔵 ASIGNADOS A MOTORIZADO</option>
          <option value="IN_TRANSIT">🟣 EN RUTA / TRANSITO</option>
          <option value="DELIVERED">🟢 ENTREGADOS (Completados)</option>
          <option value="CANCELLED">🔴 CANCELADOS</option>
        </select>

        <!-- Filtro por Tipo de Flota -->
        <select
          v-model="serviceFilter"
          class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-semibold focus:outline-none text-slate-700 dark:text-slate-200"
        >
          <option value="">Toda la Flota</option>
          <option value="MOTO_EXPRESS">🛵 Moto Express</option>
          <option value="CARRO_CARGA">🚗 Carro / Hatchback</option>
          <option value="CAMIONETA_B2B">🛻 Camioneta Pick-up</option>
        </select>
      </div>

      <!-- Tabla Operativa de Despacho -->
      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Guía & Fecha</th>
                <th class="px-6 py-4 font-semibold">Cliente & WhatsApp</th>
                <th class="px-6 py-4 font-semibold">Ruta (Origen → Destino)</th>
                <th class="px-6 py-4 font-semibold text-right">Flete / COD</th>
                <th class="px-6 py-4 font-semibold">Motorizado Asignado</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="7" class="px-6 py-8 text-center text-slate-500">
                  <Loader2 class="w-6 h-6 animate-spin mx-auto mb-2 text-[#F36421]" />
                  Cargando órdenes y despachos de LLEVO...
                </td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="7" class="px-6 py-8 text-center text-slate-500">
                  No hay solicitudes de envío que coincidan con los filtros.
                </td>
              </tr>

              <tr
                v-else
                v-for="order in filteredList"
                :key="order.id"
                class="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors group"
              >
                <!-- Guía & Fecha -->
                <td class="px-6 py-4">
                  <div class="font-mono font-bold text-slate-900 dark:text-white">
                    #{{ order.tracking_code || `LLEVO-${order.id}` }}
                  </div>
                  <div class="text-[11px] text-slate-400 mt-0.5">
                    {{ formatDateTime(order.created_at) }}
                  </div>
                  <span class="inline-flex items-center gap-1 mt-1 px-2.5 py-0.5 rounded-lg text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    {{ getFleetIcon(order.service_type) }} {{ getFleetLabel(order.service_type) }}
                  </span>
                </td>

                <!-- Cliente & Teléfono -->
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900 dark:text-slate-100 line-clamp-1">
                    {{ order.client_name || 'Cliente Particular' }}
                  </div>
                  <div class="text-xs text-slate-500 font-mono mt-0.5">
                    📱 {{ order.client_phone || 'Sin teléfono' }}
                  </div>
                  <div v-if="order.receiver_name" class="text-[11px] text-slate-400 mt-1 truncate max-w-[150px]">
                    Recibe: {{ order.receiver_name }}
                  </div>
                </td>

                <!-- Ruta (Origen → Destino) -->
                <td class="px-6 py-4 max-w-[240px]">
                  <div class="space-y-1 text-xs">
                    <div class="flex items-start gap-1.5 text-slate-700 dark:text-slate-300">
                      <span class="text-emerald-500 font-bold">A:</span>
                      <span class="line-clamp-1" :title="order.pickup_address">{{ order.pickup_address }}</span>
                    </div>
                    <div class="flex items-start gap-1.5 text-slate-700 dark:text-slate-300">
                      <span class="text-red-500 font-bold">B:</span>
                      <span class="line-clamp-1" :title="order.dropoff_address">{{ order.dropoff_address }}</span>
                    </div>
                    <div class="text-[11px] font-mono text-slate-400">
                      📏 {{ order.distance_km || 0 }} km (Aprox. {{ order.duration_mins || 0 }} min)
                    </div>
                  </div>
                </td>

                <!-- Flete & COD -->
                <td class="px-6 py-4 text-right font-mono">
                  <div class="font-bold text-[#F36421] text-sm">
                    C$ {{ formatMoney(order.total_delivery_fee) }}
                  </div>
                  <div v-if="order.product_value_cod > 0" class="text-xs font-bold text-slate-700 dark:text-slate-300 mt-0.5">
                    COD: C$ {{ formatMoney(order.product_value_cod) }}
                  </div>
                  <div class="text-[10px] text-emerald-600 mt-0.5">
                    Agencia: C$ {{ formatMoney(order.agency_cut || (order.total_delivery_fee * 0.25)) }}
                  </div>
                </td>

                <!-- Motorizado Asignado -->
                <td class="px-6 py-4">
                  <!-- Si el pedido ya tiene chofer asignado -->
                  <div v-if="order.driver_id && order.driver_name" class="space-y-1">
                    <div class="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 text-xs">
                      <UserCheck class="w-3.5 h-3.5 text-blue-500" />
                      {{ order.driver_name }}
                    </div>
                    <div class="text-[11px] font-mono text-slate-500">
                      Placa: {{ order.driver_plate || 'N/D' }}
                    </div>
                    <button
                      @click="openAssignModal(order)"
                      class="text-[10px] text-blue-600 hover:text-blue-800 underline font-semibold cursor-pointer"
                    >
                      Cambiar chofer
                    </button>
                  </div>

                  <!-- Si está PENDIENTE (Sin Chofer) -->
                  <div v-else>
                    <button
                      @click="openAssignModal(order)"
                      class="px-3 py-1.5 bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/30 dark:hover:bg-orange-900/50 text-[#F36421] border border-[#F36421]/30 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                    >
                      <Truck class="w-3.5 h-3.5" />
                      Asignar
                    </button>
                  </div>
                </td>

                <!-- Estado -->
                <td class="px-6 py-4">
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border"
                    :class="getStatusClasses(order.status)"
                  >
                    <span class="w-1.5 h-1.5 rounded-full" :class="getStatusDot(order.status)"></span>
                    {{ getStatusLabel(order.status) }}
                  </span>
                </td>

                <!-- Acciones Rápida -->
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end items-center gap-1.5">
                    <!-- Enviar resumen por WhatsApp -->
                    <button
                      @click="sendWhatsAppReminder(order)"
                      class="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 rounded-lg transition-colors"
                      title="Reenviar Orden por WhatsApp"
                    >
                      <Send class="w-4 h-4" />
                    </button>

                    <!-- Marcar Entregada rápidamente -->
                    <button
                      v-if="order.status === 'ASSIGNED' || order.status === 'IN_TRANSIT'"
                      @click="markOrderAsDelivered(order)"
                      class="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                      title="Marcar como Entregada"
                    >
                      <CheckCircle2 class="w-4 h-4" />
                    </button>

                    <!-- Eliminar / Cancelar -->
                    <button
                      @click="deleteOrder(order)"
                      class="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                      title="Cancelar / Eliminar Orden"
                    >
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pie de Tabla -->
        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
          <span>Mostrando {{ filteredList.length }} órdenes en pantalla</span>
          <button
            @click="loadOrders"
            class="text-xs font-semibold text-[#F36421] hover:underline cursor-pointer"
          >
            🔄 Actualizar Listado
          </button>
        </div>
      </div>
    </div>

    <!-- ================================================================= -->
    <!-- MODAL RÁPIDO: ASIGNAR MOTORIZADO A LA ORDEN -->
    <!-- ================================================================= -->
    <div
      v-if="showAssignModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in"
    >
      <div class="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">

        <!-- Encabezado Modal -->
        <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
          <div>
            <h3 class="font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <Truck class="w-5 h-5 text-[#F36421]" />
              Asignar Motorizado a Orden
            </h3>
            <p class="text-xs text-slate-500 font-mono">
              Guía: #{{ selectedOrder?.tracking_code || `LLEVO-${selectedOrder?.id}` }} (C$ {{ selectedOrder?.total_delivery_fee }})
            </p>
          </div>
          <button @click="showAssignModal = false" class="text-slate-400 hover:text-slate-600">
            <X class="w-5 h-5" />
          </button>
        </div>

        <!-- Cuerpo Modal -->
        <div class="p-6 space-y-4">
          <div>
            <label class="text-[10px] font-bold text-slate-500 uppercase mb-1 block">
              * Seleccionar Conductor de la Flota (LLEVO)
            </label>
            <select
              v-model="selectedDriverId"
              class="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#F36421] text-slate-800 dark:text-white"
            >
              <option value="" disabled>Selecciona un motorizado activo...</option>
              <option
                v-for="driver in driversList"
                :key="driver.id"
                :value="driver.id"
              >
                🛵 {{ driver.first_name }} {{ driver.last_name }} — Placa: {{ driver.license_plate || 'N/D' }}
              </option>
            </select>
          </div>

          <div class="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-800 dark:text-blue-300 space-y-1">
            <p class="font-bold">ℹ️ ¿Qué sucederá al confirmar?</p>
            <p class="text-[11px]">1. La orden pasará a estado <b>ASIGNADO</b>.</p>
            <p class="text-[11px]">2. El motorizado recibirá el pago del <b>75% (C$ {{ formatMoney((selectedOrder?.total_delivery_fee || 0) * 0.75) }})</b>.</p>
          </div>
        </div>

        <!-- Pie de Modal -->
        <div class="px-6 py-4 bg-slate-50 dark:bg-slate-800 border-t border-slate-100 dark:border-slate-800 flex justify-end gap-2">
          <button
            @click="showAssignModal = false"
            type="button"
            class="px-4 py-2 text-xs font-bold text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            @click="confirmDriverAssignment"
            :disabled="!selectedDriverId || isAssigning"
            type="button"
            class="px-5 py-2 text-xs font-bold text-white bg-[#F36421] hover:bg-[#d95214] disabled:opacity-50 rounded-xl shadow-md transition-all flex items-center gap-1.5"
          >
            <Loader2 v-if="isAssigning" class="w-4 h-4 animate-spin" />
            <span>Confirmar Asignación</span>
          </button>
        </div>

      </div>
    </div>

    <!-- ================================================================= -->
    <!-- MODAL PRINCIPAL: COTIZADOR WIZARD LLEVO (Form.vue) -->
    <!-- ================================================================= -->
    <CreateOrderModal
      :show="showCreateModal"
      @close="showCreateModal = false"
      @saved="loadOrders"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import {
  Search, Plus, Package, MapPin, Truck, UserCheck,
  Phone, Trash2, CheckCircle2, X, Loader2, Send
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import Swal from 'sweetalert2';
import { useToast } from '@/composables/useToast';
import CreateOrderModal from './Form.vue';

const { showSuccess, showError } = useToast();

const ordersList = ref([]);
const driversList = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const statusFilter = ref('');
const serviceFilter = ref('');

// Estados para Modales Flotantes
const showCreateModal = ref(false);
const showAssignModal = ref(false);
const isAssigning = ref(false);
const selectedOrder = ref(null);
const selectedDriverId = ref('');

// ============================================================================
// 1. CARGAR ÓRDENES Y CONDUCTORES
// ============================================================================
const loadOrders = async () => {
  isLoading.value = true;
  try {
    const res = await apiClient.get('/orders');
    ordersList.value = Array.isArray(res.data) ? res.data : (res.data.data || []);
  } catch (e) {
    console.error('Error cargando listado de despachos:', e);
    showError('No se pudo obtener el listado de órdenes.');
  } finally {
    isLoading.value = false;
  }
};

const loadDrivers = async () => {
  try {
    const res = await apiClient.get('/drivers');
    const allDrivers = Array.isArray(res.data) ? res.data : (res.data.data || []);
    driversList.value = allDrivers.filter(d => d.status === 'ACTIVE' || !d.status);
  } catch (e) {
    console.error('Error cargando conductores:', e);
  }
};

onMounted(() => {
  loadOrders();
  loadDrivers();
});

// ============================================================================
// 2. FILTROS EN MEMORIA
// ============================================================================
const filteredList = computed(() => {
  let result = ordersList.value;

  if (statusFilter.value) {
    result = result.filter(o => o.status === statusFilter.value);
  }
  if (serviceFilter.value) {
    result = result.filter(o => o.service_type === serviceFilter.value);
  }
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(o => {
      const tracking = (o.tracking_code || `LLEVO-${o.id}`).toLowerCase();
      const client = (o.client_name || '').toLowerCase();
      const phone = (o.client_phone || '').toLowerCase();
      const pickup = (o.pickup_address || '').toLowerCase();
      const dropoff = (o.dropoff_address || '').toLowerCase();
      return tracking.includes(term) || client.includes(term) || phone.includes(term) || pickup.includes(term) || dropoff.includes(term);
    });
  }

  return result;
});

// ============================================================================
// 3. APERTURA DE MODALES (NUEVA ORDEN / ASIGNAR MOTORIZADO)
// ============================================================================
const openCreateModal = () => {
  showCreateModal.value = true;
};

const openAssignModal = (order) => {
  selectedOrder.value = order;
  selectedDriverId.value = order.driver_id || '';
  showAssignModal.value = true;
};

const confirmDriverAssignment = async () => {
  if (!selectedOrder.value || !selectedDriverId.value) return;

  isAssigning.value = true;
  try {
    const driver = driversList.value.find(d => d.id === selectedDriverId.value);
    const orderPayload = { ...selectedOrder.value };
    delete orderPayload.client_name;
    await apiClient.put(`/orders/${orderPayload.id}`, {
      driver_id: selectedDriverId.value,
      status: 'ASSIGNED'
    });

    selectedOrder.value.driver_id = selectedDriverId.value;
    selectedOrder.value.driver_name = driver ? `${driver.first_name} ${driver.last_name}` : 'Asignado';
    selectedOrder.value.driver_plate = driver?.license_plate || '';
    selectedOrder.value.status = 'ASSIGNED';

    showSuccess('✔ Motorizado asignado exitosamente');
    showAssignModal.value = false;
  } catch (error) {
    console.error('Error al asignar motorizado:', error);
    showError('No se pudo guardar la asignación del conductor en el servidor.');
  } finally {
    isAssigning.value = false;
  }
};

// ============================================================================
// 4. CAMBIO DE ESTADOS Y ELIMINAR ORDEN
// ============================================================================
const markOrderAsDelivered = async (order) => {
  try {
    await apiClient.put(`/orders/${order.id}`, {
      ...order,
      status: 'DELIVERED',
      delivered_at: new Date().toISOString()
    });
    order.status = 'DELIVERED';
    showSuccess('✔ Orden marcada como Entregada');
  } catch (e) {
    showError('No se pudo actualizar el estado de la orden.');
  }
};

const deleteOrder = (order) => {
  Swal.fire({
    title: '¿Eliminar Solicitud de Envío?',
    text: `Guía #${order.tracking_code || order.id} será cancelada del despacho.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Sí, cancelar orden',
    cancelButtonText: 'Volver'
  }).then(async (res) => {
    if (res.isConfirmed) {
      try {
        await apiClient.delete(`/orders/${order.id}`);
        ordersList.value = ordersList.value.filter(o => o.id !== order.id);
        showSuccess('Orden retirada del sistema.');
      } catch (e) {
        showError('No se pudo eliminar la solicitud.');
      }
    }
  });
};

// ============================================================================
// 5. WHATSAPP & HELPERS UI
// ============================================================================
const sendWhatsAppReminder  = (order) => {
  const phone = (order.client_phone || '').replace(/\D/g, '');
  if (!phone) return showError('Sin número de WhatsApp.');

  // Emojis codificados en Unicode estricto
  const truck = '\u{1F69A}'; // 🚚
  const pin = '\u{1F4CD}';   // 📍
  const flag = '\u{1F3C1}';  // 🏁
  const moto = '\u{1F6F5}';  // 🛵
  const money = '\u{1F4B0}'; // 💰
  const cash = '\u{1F4B5}';  // 💵

  const messageText =
    `${truck} *LLEVO | ACTUALIZACIÓN DE PEDIDO*\n` +
    `Guía: #${order.tracking_code || order.id}\n` +
    `-----------------------------------\n` +
    `${pin} *Origen:* ${order.pickup_address}\n` +
    `${flag} *Destino:* ${order.dropoff_address}\n` +
    `${moto} *Conductor:* ${order.driver_name || 'Por asignar'}\n` +
    `-----------------------------------\n` +
    `${money} *Flete:* C$ ${order.total_delivery_fee}\n` +
    (order.product_value_cod > 0 ? `${cash} *COD a Cobrar:* C$ ${order.product_value_cod}\n` : '') +
    `\n¡Gracias por preferir LLEVO Logística!`;

  const params = new URLSearchParams({
    phone: `505${phone}`,
    text: messageText
  });

  window.open(`https://api.whatsapp.com/send?${params.toString()}`, '_blank');
};

const formatMoney = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

const formatDateTime = (isoString) => {
  if (!isoString) return '--';
  const d = new Date(isoString);
  return d.toLocaleDateString('es-NI', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getFleetIcon = (type) => {
  const icons = { 'MOTO_EXPRESS': '🛵', 'CARRO_CARGA': '🚗', 'CAMIONETA_B2B': '🛻' };
  return icons[type] || '📦';
};

const getFleetLabel = (type) => {
  const labels = { 'MOTO_EXPRESS': 'Moto Express', 'CARRO_CARGA': 'Carro', 'CAMIONETA_B2B': 'Camioneta' };
  return labels[type] || 'Estándar';
};

const getStatusClasses = (status) => {
  const map = {
    'PENDING': 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300',
    'ASSIGNED': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400',
    'IN_TRANSIT': 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400',
    'DELIVERED': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400',
    'CANCELLED': 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-400'
  };
  return map[status] || map['PENDING'];
};

const getStatusDot = (status) => {
  const map = {
    'PENDING': 'bg-amber-500 animate-pulse',
    'ASSIGNED': 'bg-blue-500',
    'IN_TRANSIT': 'bg-purple-500 animate-pulse',
    'DELIVERED': 'bg-emerald-500',
    'CANCELLED': 'bg-red-500'
  };
  return map[status] || 'bg-amber-500';
};

const getStatusLabel = (status) => {
  const map = {
    'PENDING': 'Por Asignar Chofer',
    'ASSIGNED': 'Asignado',
    'IN_TRANSIT': 'En Ruta',
    'DELIVERED': 'Entregado',
    'CANCELLED': 'Cancelado'
  };
  return map[status] || 'Pendiente';
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.25s ease-out forwards;
}
</style>